/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { MediaCarousel } from '@euler/app/components/media/MediaCarousel';
import { Typography } from '@euler/app/components/typography';
import { OrderExtra } from '@euler/app/flows/order/types';
import {
  kDefaultDateFormat,
  kDefaultDatetimeFormat,
  normalizedExtendedPropertiesFromOrderExtra,
} from '@euler/functions/order/extendedPropertyUtil';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { MediaObject, StoreConfiguration } from '@euler/model';
import { isNotNull, onErrorIgnore } from '@euler/utils';
import { dateFromValue } from '@euler/utils/datetime';
import { withErrorHandler } from '@euler/utils/withErrorHandler';
import format from 'date-fns/format';
import { memo } from 'react';
import { View } from 'react-native';
import { OrderInfoCell as Cell } from './Cell';

export const ExtendedPropertiesCells = memo(
  ({
    detail,
    storeConfiguration,
  }: {
    detail: OrderExtra;
    storeConfiguration: StoreConfiguration;
  }) => {
    const { rpx } = useResponsiveContext();
    const extendedProps = normalizedExtendedPropertiesFromOrderExtra(
      detail,
      storeConfiguration,
    );
    return (
      <>
        {extendedProps?.map(prop => {
          const def = storeConfiguration.extendedOrderProperties?.find(
            x => x.id === prop.id,
          );
          if (!def) return null;

          if (def.type === 'text') {
            if (prop.value != null && typeof prop.value !== 'string') {
              return null;
            }
            return (
              <Cell
                title={def.name}
                detail={prop.value as string}
                key={prop.id}
                note={
                  prop.remark?.trim()
                    ? `备注: ${prop.remark.trim()}`
                    : undefined
                }
              />
            );
          }

          if (def.type === 'switch') {
            if (prop.value != null && typeof prop.value !== 'boolean') {
              return null;
            }
            return (
              <Cell
                title={def.name}
                detail={
                  prop.value
                    ? def.yesLabel?.trim() || '是'
                    : def.noLabel?.trim() || '否'
                }
                key={prop.id}
                note={
                  prop.remark?.trim()
                    ? `备注: ${prop.remark.trim()}`
                    : undefined
                }
              />
            );
          }

          if (def.type === 'datetime') {
            return (
              <Cell
                title={def.name}
                detail={
                  (typeof prop.value === 'string' ||
                    prop.value instanceof Date) &&
                  prop.value
                    ? withErrorHandler(() => {
                        if (typeof prop.value !== 'string') return undefined;
                        const dateValue = dateFromValue(prop.value);
                        if (dateValue == null) return '';
                        const formatString = def.displayFormat?.trim();
                        return format(
                          dateValue,
                          formatString ||
                            (def.dateOnly
                              ? kDefaultDateFormat
                              : kDefaultDatetimeFormat),
                        );
                      }, onErrorIgnore) ?? ''
                    : ''
                }
                key={prop.id}
                note={
                  prop.remark?.trim()
                    ? `备注: ${prop.remark.trim()}`
                    : undefined
                }
              />
            );
          }

          if (def.type === 'select') {
            if (typeof prop.value === 'boolean') {
              return null;
            }

            const values =
              typeof prop.value === 'string' ? [prop.value] : prop.value;
            const labels = values
              .map(
                value =>
                  def.options.find(x => (x.value ?? x.defaultValue) === value)
                    ?.label,
              )
              .filter(isNotNull);

            return (
              <Cell
                title={def.name}
                key={prop.id}
                note={
                  prop.remark?.trim()
                    ? `备注: ${prop.remark.trim()}`
                    : undefined
                }
              >
                <View
                  css={`
                    align-items: flex-end;
                  `}
                >
                  {labels.map((label, index) => (
                    <View
                      key={index}
                      css={`
                        border-width: 0px;
                        border-bottom-width: 1px;
                        border-bottom-color: #ddd;
                        border-style: solid;
                      `}
                    >
                      <Typography.Label
                        light
                        size={rpx(12)}
                        color="rgb(51, 51, 51)"
                      >
                        {`${label}`}
                      </Typography.Label>
                    </View>
                  ))}
                </View>
              </Cell>
            );
          }

          if (def.type === 'assets') {
            if (
              prop.value == null ||
              prop.extra?.type !== 'assets' ||
              prop.extra.content.length === 0
            ) {
              return null;
            }
            return (
              <Cell
                title={def.name}
                key={prop.id}
                note={
                  prop.remark?.trim()
                    ? `备注: ${prop.remark.trim()}`
                    : undefined
                }
              >
                <MediaCarousel
                  display="list"
                  size={rpx(44)}
                  itemSpacer={rpx(4)}
                  style={{ justifyContent: 'flex-end' }}
                  playIconSize={rpx(12)}
                  itemStyle={{
                    borderRadius: rpx(8),
                    overflow: 'hidden',
                    marginRight: 0,
                    marginLeft: rpx(4),
                    marginTop: rpx(2),
                    marginBottom: rpx(2),
                  }}
                  medias={prop.extra.content.map<MediaObject>(x => ({
                    id: x.id,
                    type: x.type,
                    url: x.url,
                  }))}
                />
              </Cell>
            );
          }

          return null;
        })}
      </>
    );
  },
);
